import ShipmentNewsletterForm from "@/app/shared/orders/tracking/shipment-newsletter";
import Timeline from "@/app/shared/orders/tracking/timeline";
import { useCopyToClipboard } from "@/hooks/use-copy-to-clipboard";
import routes from "@/routers/routes";
import { Order, OrderStatus, Track } from "@/types";
import cn from "@/utils/class-names";
import { useState } from "react";
import { toast } from "react-hot-toast";
import {
    PiBasket,
    PiCheckCircle,
    PiContactlessPayment,
    PiCopySimple,
    PiEnvelopeLight,
    PiGitPullRequest,
    PiMoney,
    PiMoped,
    PiPackage,
} from "react-icons/pi";
import { Link, useSearchParams } from "react-router-dom";
import { Button, Text, Title } from "rizzui";

export default function TrackingOverview({
    order,
    tracks,
    className,
}: {
    order: Order;
    tracks: Track[];
    className?: string;
}) {
    return (
        <div
            className={cn(
                "grid grid-cols-1 gap-10 lg:grid-cols-2 xl:gap-20",
                className,
            )}
        >
            <div>
                <TrackingSummary order={order} />
            </div>
            <div>
                <Timeline data={tracks} showmoreButton={true} order="desc" />
            </div>
        </div>
    );
}

interface TrackingSummaryProps {
    order: Order;
}

export function TrackingSummary({ order }: TrackingSummaryProps) {
    const [isCopied, setCopied] = useState(false);
    const [_, copyToClipboard] = useCopyToClipboard();

    function handleCopyToClipboard(value: string) {
        copyToClipboard(value);
        toast.success(<b>{`Copied '${value}' to clipboard`}</b>);

        setCopied(() => true);
        setTimeout(() => {
            setCopied(() => false);
        }, 2000);
    }
    return (
        <>
            <Text className="mb-2 text-gray-700">Tracking Number:</Text>
            <Title
                as="h2"
                className="mb-3 text-2xl font-bold text-gray-700 3xl:text-3xl"
            >
                {order?.trackingNumber}
            </Title>

            <div className="mb-7 flex items-center gap-x-5">
                <Button
                    variant="text"
                    onClick={() => handleCopyToClipboard(order.id as string)}
                    className="inline-flex h-auto w-auto items-center gap-1 px-0 py-0 font-normal"
                >
                    <PiCopySimple className="h-5 w-5" />
                    <Text as="span" className="text-gray-700">
                        {isCopied ? "Copied" : "Copy"}
                    </Text>
                </Button>
                <Text className="inline-flex items-center gap-1">
                    <PiGitPullRequest className="h-5 w-5" />
                    <Text as="span" className="text-gray-700">
                        View more details on{" "}
                        <span className="font-semibold">
                            {" "}
                            <Link
                                to={routes.orders.orderDetails(
                                    order.id as string,
                                )}
                                className="text-primary"
                            >
                                here
                            </Link>
                        </span>
                    </Text>
                </Text>
            </div>

            <div className="max-w-[505px] rounded-lg border border-l-4 border-primary bg-primary-lighter/10 p-7">
                <Title
                    as="h3"
                    className="mb-3 text-xl font-semibold text-gray-900"
                >
                    {order.status}
                </Title>
                {order.status === OrderStatus.Delivered ? (
                    <Text className="mb-2 text-gray-500 md:text-base md:leading-relaxed">
                        This shipment has been delivered to the address below.
                        If you have any questions, please contact our customer
                        service team.
                        <Text as="span" className="font-semibold text-gray-700">
                            {order?.shippingAddress}
                        </Text>
                    </Text>
                ) : order.status === OrderStatus.Requested ? (
                    <Text className="mb-2 text-gray-500 md:text-base md:leading-relaxed">
                        This shipment has been requested. If you have any
                        questions, please contact our customer service team.
                    </Text>
                ) : order.status === OrderStatus.Cancelled ? (
                    <Text className="mb-2 text-gray-500 md:text-base md:leading-relaxed">
                        This shipment has been cancelled. If you have any
                        questions, please contact our customer service team.
                    </Text>
                ) : order.status === OrderStatus.Processing ? (
                    <Text className="mb-2 text-gray-500 md:text-base md:leading-relaxed">
                        This shipment is being processed. If you have any
                        questions, please contact our customer service team.
                    </Text>
                ) : order.status === OrderStatus.Purchased ? (
                    <>
                        <Text className="mb-2 text-gray-500 md:text-base md:leading-relaxed">
                            This shipment has been purchased. If you have any
                            questions, please contact our customer service team.
                        </Text>
                        <div className="flex items-center gap-5 mt-7">
                            <PiPackage className="h-8 w-8 text-primary" />
                            <div>
                                <Text className="text-gray-500">
                                    Product Name
                                </Text>
                                <Text className="text-gray-700">
                                    {order?.product?.name}
                                </Text>
                            </div>
                            <PiEnvelopeLight className="h-8 w-8 text-primary" />
                            <div>
                                <Text className="text-gray-500">
                                    Product Category
                                </Text>
                                <Text className="text-gray-700">
                                    {order?.product?.category?.name}
                                </Text>
                            </div>
                        </div>
                    </>
                ) : order.status === OrderStatus.InTransit ? (
                    <>
                        <Text className="mb-2 text-gray-500 md:text-base md:leading-relaxed">
                            This shipment is in transit. If you have any
                            questions, please contact our customer service team.
                        </Text>
                        <div className="flex items-center gap-5">
                            <PiMoped className="h-8 w-8 text-primary" />
                            <div>
                                <Text className="text-gray-500">
                                    Estimated Delivery
                                </Text>
                                <Text className="text-gray-700">
                                    {new Date(
                                        order?.estimatedDeliveryDate,
                                    ).toDateString()}
                                </Text>
                            </div>
                        </div>
                    </>
                ) : null}
                <hr className="my-7 border-t border-gray-200" />
                <h4 className="mb-3 text-lg font-semibold text-gray-700">
                    Contact Information
                </h4>
                <div className="flex items-center gap-5 mt-7">
                    <PiMoney className="h-8 w-8 text-primary" />
                    <div>
                        <Text className="text-gray-500">Phone</Text>
                        <Text className="text-gray-700">+234 123 456 7890</Text>
                    </div>
                    <PiEnvelopeLight className="h-8 w-8 text-primary" />
                    <div>
                        <Text className="text-gray-500">Email</Text>
                        <Text className="text-gray-700">support@afro.com</Text>
                    </div>
                </div>
            </div>
        </>
    );
}

export function ShipmentSubscription({ className }: { className?: string }) {
    return (
        <div className={cn(className)}>
            <Text className="mb-3">
                Want update on this shipment? Enter your email address & we will
                do the rest!
            </Text>
            <ShipmentNewsletterForm
                placeholderText="smith@example.com"
                buttonClassName="rounded-lg max-w-[118px]"
                buttonLabel="Submit"
                className="w-full max-w-3xl"
            />
        </div>
    );
}
